import { z } from 'zod'
import { APP_API_REFUSAL_CODES, APP_API_REFUSALS } from './refusals.generated.js'

/**
 * Policy refusal codes from docs/RUNTIME.md "Policy" (deletion wave, ADR-0010:
 * the mandate, notional, position and router codes went with algo.boo's own
 * execution; arbitrage enforces the customer-signed permission caps). `packages/policy`
 * returns these; the API answers them as 409 with `error.code` set to the
 * first code and `details.codes` holding all of them.
 */
export const POLICY_REFUSAL_CODES = [
  'PERMISSION_INACTIVE',
  'PERMISSION_EXPIRED',
  'ASSET_NOT_ALLOWED',
  'VENUE_NOT_ALLOWED',
  'SLIPPAGE_EXCEEDED',
  'PRICE_IMPACT_EXCEEDED',
  'LIQUIDITY_TOO_LOW',
  'QUOTE_STALE',
  'QUOTE_MISSING',
  'PROPOSAL_EXPIRED',
  'DUPLICATE_PROPOSAL',
  'DUPLICATE_EXIT',
  'INVALID_INTENT',
  'INSUFFICIENT_POSITION',
  'ACCOUNT_FROZEN',
  // Spec v3 (docs/INTENTS.md §4 step 7, runtime-integration): signal age or deadline past (copy lag), our quote
  // too far above the leader's price, token safety failed or missing for a buy, strategy copy limits, and a
  // live permission for a spec-v3 version that has not passed its trial gate.
  'SIGNAL_STALE',
  'CHASE_EXCEEDED',
  'TOKEN_UNSAFE',
  'COPY_LIMIT',
  'TRIAL_REQUIRED',
  // Codebase review 2, finding 5: an entry whose USD value cannot be priced is refused, never checked against $0.
  'PRICE_UNAVAILABLE',
] as const
export const PolicyRefusalCode = z.enum(POLICY_REFUSAL_CODES)
export type PolicyRefusalCode = z.output<typeof PolicyRefusalCode>

/**
 * Refusals surfaced from arbitrage (market-api, arb-quote-api, customer
 * path). Adapters raise these as typed errors, never empty results.
 */
export const ARBITRAGE_REFUSAL_CODES = [
  'QUOTE_TOO_OLD',
  'WALLET_NOT_YOURS',
  'ACCOUNT_SUSPENDED',
  'NOT_YET_DERIVED',
  'DECODED_LAYER_UNCONFIGURED',
] as const
export const ArbitrageRefusalCode = z.enum(ARBITRAGE_REFUSAL_CODES)
export type ArbitrageRefusalCode = z.output<typeof ArbitrageRefusalCode>

/**
 * The arbitrage App API v1 refusal enum and each code's status and sentence: generated from the platform's
 * `schema/app-api/v1/refusals.json` by `scripts/gen-refusals.mjs` (lane BOOCLEAN1), never kept by hand.
 */
export { APP_API_REFUSAL_CODES, APP_API_REFUSALS } from './refusals.generated.js'
export const AppApiRefusalCode = z.enum(APP_API_REFUSAL_CODES)
export type AppApiRefusalCode = z.output<typeof AppApiRefusalCode>

export function isAppApiRefusalCode(code: string): code is AppApiRefusalCode {
  return (APP_API_REFUSAL_CODES as readonly string[]).includes(code)
}

/** One entry per distinct value, as a non-empty tuple `z.enum` accepts (the code lists overlap on a few names). */
function distinct<T extends string>(xs: readonly T[]): [T, ...T[]] {
  return [...new Set(xs)] as [T, ...T[]]
}

/**
 * Execution refusals raised after policy approval, when the platform cannot submit (proposal `failed`, the code in
 * `refusal_codes`). `AGENT_INACTIVE`: the agent was paused or retired between approval and execution (proposal
 * `refused`; the re-validation before submitting otherwise uses PERMISSION_INACTIVE, PERMISSION_EXPIRED,
 * ACCOUNT_FROZEN, PROPOSAL_EXPIRED and a re-quoted policy verdict). `EXECUTION_CAP_EXCEEDED`: arbitrage refused the order for a cap of the customer-signed permission
 * (`PER_ORDER_CAP`, `WINDOW_CAP`, `EXPOSURE_CAP`); `EXECUTION_REFUSED`: any other arbitrage refusal without a more
 * specific policy code (the arbitrage code is kept next to it in the evidence).
 */
export const EXECUTION_REFUSAL_CODES = ['AGENT_INACTIVE', 'EXECUTION_CAP_EXCEEDED', 'EXECUTION_REFUSED'] as const
export const ExecutionRefusalCode = z.enum(EXECUTION_REFUSAL_CODES)
export type ExecutionRefusalCode = z.output<typeof ExecutionRefusalCode>

/**
 * Agent-initiated token launches (`agent.launch_token`, `POST /agent/token/launch`; docs/CONTRACTS.md "Decisions
 * after review 2"): the creator's on-chain `LaunchPolicy` is missing, expired or does not cover the parameters, the
 * executor cannot pay `LAUNCH_GAS_ESTIMATE × maxFeePerGas`, the agent already has a token, or the executor may not
 * launch (not bound or not enrolled).
 */
export const LAUNCH_REFUSAL_CODES = [
  'LAUNCH_POLICY_MISSING',
  'LAUNCH_POLICY_EXPIRED',
  'LAUNCH_POLICY_MISMATCH',
  'LAUNCH_GAS_INSUFFICIENT',
  'TOKEN_ALREADY_LAUNCHED',
  'LAUNCH_NOT_AUTHORIZED',
] as const
export const LaunchRefusalCode = z.enum(LAUNCH_REFUSAL_CODES)
export type LaunchRefusalCode = z.output<typeof LaunchRefusalCode>

/**
 * What the social product does not do, by name (lane BOOUX1: the sixteen items ALGOBOO2 left as "not yet", A2-1 to
 * A2-16, each either built or answered as one of these). A read carries one as `{ code, words }` (`ProductLimit`,
 * `api/limits.ts`) where the figure would be, never a zero or an empty list; a write that asks for it is refused with
 * the code (409). The words of each are `PRODUCT_LIMITS`.
 */
export const PRODUCT_LIMIT_CODES = [
  /** A2-1: the first public mention of a token is in chats the platform keeps private. */
  'FIRST_MENTION_PRIVATE',
  /** A2-2: whether a strategy's runner is awake this second is not served to apps. */
  'RUNNER_LIVENESS_NOT_SERVED',
  /** A2-3: trades of strategies whose orders carry no app are not published to apps. */
  'TRADES_OUTSIDE_APP_NOT_PUBLISHED',
  /** A2-4: the price move while a copy waits out its delay is not replayed. */
  'SHADOW_DELAY_MOVE_NOT_REPLAYED',
  /** A2-7: a remix changes declared settings; a new line of the program is the desk's authoring. */
  'REMIX_PROGRAM_LINE_NOT_OFFERED',
  /** A2-8: the platform recorded no fork block (its App API had no node to ask when the fork was made). */
  'FORK_BLOCK_UNKNOWN',
  /** A2-12: the scout outputs a trade's guards read are not recorded with the trade. */
  'SCOUT_INPUTS_NOT_RECORDED',
  /** A2-13: last week's orders are not replayed under a price rule. */
  'RULE_REPLAY_NOT_SERVED',
  /** A2-14: no equity is observed within a day. */
  'INTRADAY_EQUITY_NOT_RECORDED',
  /** A2-16: the agents of a challenge are not run on one shared paper account each. */
  'SHARED_PAPER_ACCOUNT_NOT_RUN',
] as const
export const ProductLimitCode = z.enum(PRODUCT_LIMIT_CODES)
export type ProductLimitCode = z.output<typeof ProductLimitCode>

/**
 * Rooms (lane CHATCORE1, docs/ROOMS.md): why a room refuses a join, a message or a moderation step. Permanent and
 * additive like every code here; each one's sentence is `ROOM_REFUSAL_WORDS` (`api/rooms.ts`), sent as the error's
 * message, and the web's copy says the same.
 */
export const ROOM_REFUSAL_CODES = [
  'ROOM_NOT_MEMBER',
  'ROOM_GATE_NOT_MET',
  'ROOM_GATE_UNCHECKABLE',
  'ROOM_GATE_NOT_OFFERED',
  'ROOM_INVITE_REQUIRED',
  'ROOM_INVITE_INVALID',
  'ROOM_BANNED',
  'ROOM_ARCHIVED',
  'ROOM_FULL',
  'ROOM_LIMIT_REACHED',
  'ROOM_SLOW_MODE',
  'ROOM_MESSAGE_TOO_LONG',
  'ROOM_POST_NOT_ALLOWED',
  'ROOM_ROLE_REQUIRED',
  'ROOM_DM_NOT_ALLOWED',
  'ROOM_KIND_NOT_OFFERED',
  'ROOM_SLUG_TAKEN',
  'ROOM_MESSAGE_DELETED',
  'ROOM_AGENT_NOT_ALLOWED',
  'ROOM_PIN_LIMIT',
  'ROOM_OWNER_CANNOT_LEAVE',
] as const
export const RoomRefusalCode = z.enum(ROOM_REFUSAL_CODES)
export type RoomRefusalCode = z.output<typeof RoomRefusalCode>

/**
 * The platform's principal cut (lane BOO2, the platform's v2.3): a Boo is a platform agent. `AGENT_REGISTRATION_RETIRED`
 * (410): algo.boo's own registration of an agent is retired since the cut (`PRINCIPAL_CUT_DATE`); the Boo registers
 * under its platform agent. `AGENT_PRINCIPAL_PENDING` (409): the Boo's platform agent is not made or not active yet, so
 * a question, a report or an action waits; `details.state` names where it stands. `MANDATE_REQUIRED` (409): an order or
 * a payout is an action under the owner's mandate, and the Boo has no standing mandate: issue one first.
 */
export const PRINCIPAL_CUT_CODES = ['AGENT_REGISTRATION_RETIRED', 'AGENT_PRINCIPAL_PENDING', 'MANDATE_REQUIRED'] as const
export const PrincipalCutCode = z.enum(PRINCIPAL_CUT_CODES)
export type PrincipalCutCode = z.output<typeof PrincipalCutCode>

/** Budget refusal from docs/ECONOMICS.md. */
export const BUDGET_REFUSAL_CODES = ['BUDGET_EXHAUSTED'] as const
export const BudgetRefusalCode = z.enum(BUDGET_REFUSAL_CODES)
export type BudgetRefusalCode = z.output<typeof BudgetRefusalCode>

export const RefusalCode = z.enum([...POLICY_REFUSAL_CODES, ...ARBITRAGE_REFUSAL_CODES, ...BUDGET_REFUSAL_CODES, ...EXECUTION_REFUSAL_CODES, ...LAUNCH_REFUSAL_CODES])
export type RefusalCode = z.output<typeof RefusalCode>

export function isPolicyRefusalCode(code: string): code is PolicyRefusalCode {
  return (POLICY_REFUSAL_CODES as readonly string[]).includes(code)
}
export function isArbitrageRefusalCode(code: string): code is ArbitrageRefusalCode {
  return (ARBITRAGE_REFUSAL_CODES as readonly string[]).includes(code)
}

/** Generic API error codes (docs/API.md "Conventions"). */
export const BASE_ERROR_CODES = [
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'INVALID_REQUEST',
  'NOT_FOUND',
  'CONFLICT',
  'RATE_LIMITED',
  'NO_TOKEN',
  'IDEMPOTENCY_CONFLICT',
  'INTERNAL',
  /** The action needs a paid plan or an agent subscription. */
  'SUBSCRIPTION_REQUIRED',
  /** Authorizing an agent (or resuming a permission) needs the current risk disclosure acknowledged. */
  'RISK_ACK_REQUIRED',
  /** A permission grant or an agent tick under a lapsed agent subscription. */
  'SUBSCRIPTION_EXPIRED',
  /** An overdue invoice pauses the user's permissions until it is paid. */
  'INVOICE_OVERDUE',
  /** The route is declared in the contract but not implemented yet (501). */
  'NOT_IMPLEMENTED',
  /** A request with the same Idempotency-Key is still running and did not finish within the wait (409). */
  'IDEMPOTENCY_IN_PROGRESS',
  /** An agent-API write by a paused or retired agent (403); the owner resumes the agent or revokes its keys. */
  'AGENT_PAUSED',
  /** The venue exists in the catalog but is not live yet (409, `details.venue`); join its waitlist instead. */
  'VENUE_NOT_AVAILABLE',
  /** A proposal or program for a venue other than the agent's venue of record (409, `details { venue, agentVenue }`). */
  'VENUE_MISMATCH',
  /** Agent creation (interview, confirm) without a paid, unused creation credit (402); pay `POST /agents/creation` first. */
  'CREATION_CREDIT_REQUIRED',
  /** Deciding an approval whose `expiresAt` passed while pending (409); it is now `expired` (docs/CONVERSATIONS.md). */
  'APPROVAL_EXPIRED',
  /** Approving a rejected approval, or rejecting an approved, executed or failed one (409, `details.state`). */
  'APPROVAL_NOT_PENDING',
  /** Posting to an archived thread (409); reopen it with `PATCH /threads/:id { state: open }`. */
  'THREAD_ARCHIVED',
  /** Creation v2: the ticker is taken by an agent token or an open launch (409, `details { ticker, suggestion }`). */
  'TICKER_TAKEN',
  /** Creation v2: the creation fee was not paid (402): the payment intent expired or was never confirmed. */
  'FEE_UNPAID',
  /** Creation v2: the token launch was refused or reverted (409, `details.code` = the launch refusal). */
  'LAUNCH_FAILED',
] as const

/** Every `error.code` the API may return. */
export const ErrorCode = z.enum(
  distinct([
    ...BASE_ERROR_CODES,
    ...POLICY_REFUSAL_CODES,
    ...ARBITRAGE_REFUSAL_CODES,
    ...APP_API_REFUSAL_CODES,
    ...BUDGET_REFUSAL_CODES,
    ...EXECUTION_REFUSAL_CODES,
    ...LAUNCH_REFUSAL_CODES,
    ...PRODUCT_LIMIT_CODES,
    ...ROOM_REFUSAL_CODES,
    ...PRINCIPAL_CUT_CODES,
  ]),
)
export type ErrorCode = z.output<typeof ErrorCode>

/** Suggested HTTP status per error code. */
const APP_API_HTTP = new Map<string, number>(APP_API_REFUSALS.map((r) => [r.code, r.http]))
/** algo.boo's own codes: a code the App API shares (a policy pre-check, the credit budget) keeps algo.boo's status. */
const OWN_CODES = new Set<string>([
  ...BASE_ERROR_CODES,
  ...POLICY_REFUSAL_CODES,
  ...ARBITRAGE_REFUSAL_CODES,
  ...BUDGET_REFUSAL_CODES,
  ...EXECUTION_REFUSAL_CODES,
  ...LAUNCH_REFUSAL_CODES,
  ...PRODUCT_LIMIT_CODES,
  ...ROOM_REFUSAL_CODES,
  ...PRINCIPAL_CUT_CODES,
])

/**
 * Suggested HTTP status per error code: algo.boo's own codes by the cases below; an App API code that is not also one
 * of algo.boo's by the platform's status (`APP_API_REFUSALS`, generated); the app-credential failures (`AUTH_*`,
 * `KEY_SCOPE_DENIED`) are the platform's own fault and answer 502.
 */
export function httpStatusForError(code: ErrorCode): number {
  switch (code) {
    case 'UNAUTHENTICATED':
      return 401
    case 'FORBIDDEN':
    case 'SUBSCRIPTION_REQUIRED':
    case 'RISK_ACK_REQUIRED':
    case 'SUBSCRIPTION_EXPIRED':
    case 'AGENT_PAUSED':
    case 'LAUNCH_NOT_AUTHORIZED':
    case 'ROOM_NOT_MEMBER':
    case 'ROOM_GATE_NOT_MET':
    case 'ROOM_INVITE_REQUIRED':
    case 'ROOM_BANNED':
    case 'ROOM_POST_NOT_ALLOWED':
    case 'ROOM_ROLE_REQUIRED':
    case 'ROOM_DM_NOT_ALLOWED':
    case 'ROOM_AGENT_NOT_ALLOWED':
      return 403
    case 'INVOICE_OVERDUE':
    case 'CREATION_CREDIT_REQUIRED':
    case 'FEE_UNPAID':
      return 402
    case 'INVALID_REQUEST':
    case 'ROOM_MESSAGE_TOO_LONG':
      return 400
    case 'NOT_FOUND':
    case 'NO_TOKEN':
      return 404
    case 'RATE_LIMITED':
    case 'ROOM_SLOW_MODE':
      return 429
    case 'INTERNAL':
      return 500
    case 'NOT_IMPLEMENTED':
      return 501
    case 'AGENT_REGISTRATION_RETIRED':
      return 410
    case 'AUTH_INVALID':
    case 'AUTH_REPLAY':
    case 'KEY_SCOPE_DENIED':
      return 502
    case 'NOT_YET_DERIVED':
    case 'DECODED_LAYER_UNCONFIGURED':
    case 'ROOM_GATE_UNCHECKABLE':
      return 503
    default:
      // An App API code that is not also one of algo.boo's own: the platform's status (generated).
      return OWN_CODES.has(code) ? 409 : (APP_API_HTTP.get(code) ?? 409)
  }
}

export const ApiError = z.object({
  error: z.object({
    code: ErrorCode,
    message: z.string(),
    details: z.unknown().optional(),
  }),
})
export type ApiError = z.output<typeof ApiError>
